import Image from 'next/image'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Container, RatingWrapper, ReviewHeader, SeenFor, Specialties, Tag, Verified, CustomLink } from './styles'
import { BodyMedium } from '../styles'
import { Rating, useMediaQuery, useTheme } from '@hermes/web-components'
import { TransformHomePageReviews } from '../../../types/homePage'
import { PATH_TO_UNAVAILABLE_IMG } from '../../../constants/global'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { ImagePlaceholder } from '../PeerEndorsementsCard/styles'

type Props = TransformHomePageReviews & {
  hrefToRedirect: string
}

const ReviewCard: FC<Props> = ({ avatar, name, specialties, review, hrefToRedirect, initials }) => {
  const translate = useTranslateMessage()
  const [cardIsFocused, setCardIsFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))

  const handleToggleFocusContainer = useCallback(() => setCardIsFocused((prev) => !prev), [])

  useEffect(() => {
    if (containerRef.current && !isDesktop) {
      if (cardIsFocused) {
        containerRef.current.focus()
      } else {
        containerRef.current.blur()
      }
    }
  }, [cardIsFocused, isDesktop])

  return (
    <Container
      ref={containerRef}
      {...(!isDesktop && {
        tabindex: '0',
        onTouchStart: handleToggleFocusContainer,
        onTouchEnd: handleToggleFocusContainer
      })}
    >
      <ReviewHeader>
        {avatar ? (
          <Image
            src={avatar || PATH_TO_UNAVAILABLE_IMG}
            alt="User avatar"
            style={{ borderRadius: '50%', cursor: 'pointer' }}
            width={40}
            height={40}
            onClick={() => window.location.assign(hrefToRedirect)}
          />
        ) : (
          <ImagePlaceholder style={{ width: '40px', height: '40px', fontSize: '20px' }}>{initials}</ImagePlaceholder>
        )}
        <div>
          <CustomLink href={hrefToRedirect}>{name}</CustomLink>
          <Specialties>{specialties}</Specialties>
        </div>
      </ReviewHeader>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <RatingWrapper>
          <Rating value={+review.rating} size="small" readOnly />
          <BodyMedium sx={{ fontWeight: 600 }}>{review.rating}</BodyMedium>
        </RatingWrapper>
        {review.isVerified && <Verified>{translate('homepage.review.card.verified')}</Verified>}
      </div>
      <BodyMedium sx={{ my: '16px', color: '#2C3341', minHeight: '100px' }}>{review.message}</BodyMedium>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {!!review.seenFor.length && (
          <>
            <SeenFor>{translate('homepage.review.card.seen-for')}</SeenFor>
            <Tag sx={{ maxWidth: '100%' }} key={review.seenFor[0].name}>
              {review.seenFor[0].name}
            </Tag>
          </>
        )}
      </div>
    </Container>
  )
}

export default ReviewCard
